import moment from "moment"
import { ChartOptions, TooltipItem } from "chart.js"

import { YYYY_MM_DD_FORMAT } from "@framework/constants/global"

import "chartjs-adapter-moment"

type Props = {
  showAxises?: boolean
  axisColor?: string
  gridColor?: string
  dateFormat?: string
  callbacks?: {
    tooltipLabel?: (item: TooltipItem<"line">) => string
  }
} & Partial<
  Record<
    "y1" | "y2",
    {
      suggestedMaxY?: number
      suggestedMinY?: number
      display?: boolean
      label?: (item: string | number) => string
    }
  >
>

export const options = (props: Props = {}): ChartOptions<"line"> => {
  const { dateFormat = YYYY_MM_DD_FORMAT } = props
  return {
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 5,
        right: 0,
        top: 5,
        bottom: 0,
      },
    },
    elements: {
      point: {
        radius: 0,
        hitRadius: 0,
      },
    },
    interaction: {
      intersect: false,
      axis: "x",
      mode: "nearest",
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: "day",
          tooltipFormat: dateFormat,
          parser: dateFormat,
        },
        ticks: {
          display: props?.showAxises,
          color: props?.axisColor ?? "white",
          maxRotation: 0,
          maxTicksLimit: 10,
          padding: 10,
        },
        grid: {
          display: false,
          drawBorder: false,
        },
      },
      y1: {
        display: !!props.y1?.display,
        suggestedMax: props.y1?.suggestedMaxY,
        suggestedMin: props.y1?.suggestedMinY,
        ticks: {
          display: props.showAxises,
          color: props.axisColor ?? "white",
          maxTicksLimit: 8,
          padding: 15,
          crossAlign: "far",
          font: {
            size: 14,
          },
          callback: props.y1?.label,
        },
        grid: {
          display: true,
          drawTicks: false,
          drawBorder: false,
          borderDash: [8, 10],
          color: props?.gridColor,
        },
      },
      y2: {
        display: !!props.y2?.display,
        suggestedMax: props.y2?.suggestedMaxY,
        suggestedMin: props.y2?.suggestedMinY,
        position: "right",
        ticks: {
          display: props.showAxises,
          color: props.axisColor ?? "white",
          maxTicksLimit: 8,
          padding: 15,
          crossAlign: "far",
          font: {
            size: 14,
          },
          callback: props.y2?.label,
        },
        grid: {
          display: true,
          drawTicks: false,
          drawBorder: false,
        },
      },
    },
    plugins: {
      tooltip: {
        axis: "x",
        mode: "nearest",
        backgroundColor: "white",
        usePointStyle: true,
        titleColor: "#2E2C4D99",
        footerColor: "#2E2C4D99",
        callbacks: {
          title: (it: TooltipItem<"line">[]) => {
            return moment(it[0]?.label, dateFormat).format("MMM DD, YYYY")
          },
          labelColor: (e) => {
            const { borderColor = "red" } = e.dataset

            const labelColor =
              typeof borderColor === "string" ? borderColor : "#2E2C4D99"

            return {
              backgroundColor: labelColor,
              borderColor: "#2E2C4D99",
              borderWidth: 1,
            }
          },
          labelPointStyle: () => ({ pointStyle: "circle", rotation: 0 }),
          labelTextColor: () => "black",
          label: props.callbacks?.tooltipLabel,
        },
      },
    },
  }
}

export default { options }
